import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  TextField,
  Alert,
  useTheme,
  alpha,
  LinearProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Smartphone as SmartphoneIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import authService from '../../services/authService';
import { extractErrorMessage } from '../../utils/apiFetch';

interface TwoFactorSetupProps {
  onComplete?: (secret: string, backupCodes: string[]) => void;
  onCancel?: () => void;
  loading?: boolean;
}

interface SetupStep {
  label: string;
  description: string;
}

const TwoFactorSetup: React.FC<TwoFactorSetupProps> = ({
  onComplete,
  onCancel,
  loading = false,
}) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [qrCode, setQrCode] = useState<string>('');
  const [secret, setSecret] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [verificationError, setVerificationError] = useState<string>('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [setupError, setSetupError] = useState<string>('');
  const [loadingSetup, setLoadingSetup] = useState(false);

  const steps: SetupStep[] = [
    {
      label: 'Authenticator App installieren',
      description: 'Installieren Sie eine Authenticator App wie Google Authenticator oder Authy',
    },
    {
      label: 'QR-Code scannen',
      description: 'Scannen Sie den QR-Code mit Ihrer Authenticator App',
    },
    {
      label: 'Code verifizieren',
      description: 'Geben Sie den 6-stelligen Code aus Ihrer App ein',
    },
    {
      label: 'Backup-Codes speichern',
      description: 'Speichern Sie diese Codes sicher für den Notfall',
    },
  ];

  const fetchSetupData = async () => {
    setLoadingSetup(true);
    setSetupError('');
    try {
      const tokens = authService.getStoredTokens();
      if (!tokens?.access_token) {
        setSetupError('Nicht eingeloggt. Bitte erneut anmelden.');
        return;
      }
      const data = await authService.enable2FA(tokens.access_token);
      setSecret(data.secret);
      setQrCode(data.qr_code);
      if (data.backup_codes) {
        setBackupCodes(data.backup_codes);
      }
    } catch (error: any) {
      setSetupError(extractErrorMessage(error, 'Fehler beim Laden der 2FA-Daten'));
    } finally {
      setLoadingSetup(false);
    }
  };

  useEffect(() => {
    fetchSetupData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleVerifyCode = async () => {
    if (verificationCode.length !== 6) {
      setVerificationError('Code muss 6 Zeichen lang sein');
      return;
    }

    setIsVerifying(true);
    setVerificationError('');

    try {
      const tokens = authService.getStoredTokens();
      if (!tokens?.access_token) {
        setVerificationError('Nicht eingeloggt.');
        return;
      }

      await authService.verify2FA(tokens.access_token, {
        token: verificationCode,
        backup_codes: backupCodes,
      });
      handleNext();
    } catch (error: any) {
      setVerificationError(extractErrorMessage(error, 'Ungültiger Code. Bitte versuchen Sie es erneut.'));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleComplete = () => {
    onComplete?.(secret, backupCodes);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Installieren Sie eine der folgenden Authenticator Apps auf Ihrem Smartphone:
            </Typography>

            <Box display="flex" gap={2} mb={3}>
              <Card variant="outlined" sx={{ p: 2, textAlign: 'center', flex: 1 }}>
                <SmartphoneIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="subtitle2" fontWeight={600}>
                  Google Authenticator
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Kostenlos für iOS und Android
                </Typography>
              </Card>

              <Card variant="outlined" sx={{ p: 2, textAlign: 'center', flex: 1 }}>
                <SmartphoneIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="subtitle2" fontWeight={600}>
                  Authy
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Backup und Sync-Funktion
                </Typography>
              </Card>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              Diese Apps generieren 6-stellige Codes alle 30 Sekunden für zusätzliche Sicherheit.
            </Alert>

            {setupError && (
              <Alert severity="error" sx={{ mb: 2 }}>{setupError}</Alert>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Scannen Sie diesen QR-Code mit Ihrer Authenticator App:
            </Typography>

            <Box display="flex" justifyContent="center" mb={3}>
              {loadingSetup ? (
                <Box sx={{ textAlign: 'center', p: 3 }}>
                  <LinearProgress sx={{ mb: 2, width: 200 }} />
                  <Typography variant="caption" color="text.secondary">
                    QR-Code wird geladen...
                  </Typography>
                </Box>
              ) : setupError ? (
                <Alert severity="error" sx={{ width: '100%' }}>
                  {setupError}
                  <Button size="small" onClick={fetchSetupData} sx={{ mt: 1 }}>
                    Erneut versuchen
                  </Button>
                </Alert>
              ) : qrCode ? (
                <Card
                  variant="outlined"
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: 2,
                  }}
                >
                  <img
                    src={qrCode}
                    alt="2FA QR-Code"
                    style={{ width: 200, height: 200, imageRendering: 'pixelated' }}
                  />
                </Card>
              ) : null}
            </Box>

            {secret && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  Alternative: Manueller Setup-Code
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      p: 1,
                      borderRadius: 1,
                      flexGrow: 1,
                      wordBreak: 'break-all',
                    }}
                  >
                    {secret}
                  </Typography>
                  <Tooltip title="Kopieren">
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(secret)}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Alert>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Geben Sie den 6-stelligen Code aus Ihrer Authenticator App ein:
            </Typography>

            <TextField
              fullWidth
              label="Verifikations-Code"
              value={verificationCode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setVerificationCode(value);
                setVerificationError('');
              }}
              error={!!verificationError}
              helperText={verificationError}
              placeholder="123456"
              inputProps={{
                style: {
                  fontSize: '1.5rem',
                  textAlign: 'center',
                  letterSpacing: '0.5rem',
                  fontFamily: 'monospace',
                },
                maxLength: 6,
              }}
              sx={{ mb: 3 }}
            />

            {isVerifying && (
              <Box mb={2}>
                <LinearProgress />
                <Typography variant="caption" color="text.secondary" textAlign="center" display="block" mt={1}>
                  Code wird überprüft...
                </Typography>
              </Box>
            )}

            <Alert severity="info">
              Der Code ändert sich alle 30 Sekunden. Stellen Sie sicher, dass Sie den aktuellen Code eingeben.
            </Alert>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Speichern Sie diese Backup-Codes sicher. Sie können diese verwenden, wenn Sie keinen Zugriff auf Ihr Smartphone haben:
            </Typography>

            {backupCodes.length === 0 && (
              <Alert severity="error" sx={{ mb: 3 }}>
                Backup-Codes konnten nicht vom Server geladen werden. Bitte 2FA deaktivieren und erneut aktivieren.
              </Alert>
            )}

            <Card
              variant="outlined"
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: alpha(theme.palette.warning.main, 0.05),
                border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Backup-Codes
                </Typography>
                <Tooltip title="Alle Codes kopieren">
                  <IconButton
                    size="small"
                    onClick={() => copyToClipboard(backupCodes.join('\n'))}
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
                {backupCodes.map((code, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      backgroundColor: alpha(theme.palette.background.default, 0.5),
                      p: 1,
                      borderRadius: 1,
                      textAlign: 'center',
                    }}
                  >
                    {code}
                  </Typography>
                ))}
              </Box>
            </Card>

            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>
                Wichtige Hinweise:
              </Typography>
              <Typography variant="body2" component="div">
                • Jeder Code kann nur einmal verwendet werden<br />
                • Speichern Sie die Codes an einem sicheren Ort<br />
                • Teilen Sie diese Codes niemals mit anderen
              </Typography>
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <SecurityIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Zwei-Faktor-Authentifizierung einrichten
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Erhöhen Sie die Sicherheit Ihres Accounts
            </Typography>
          </Box>
        </Box>

        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>
                <Typography variant="subtitle1" fontWeight={600}>
                  {step.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {step.description}
                </Typography>
              </StepLabel>
              <StepContent>
                {renderStepContent(index)}

                <Box mt={3} display="flex" gap={2}>
                  {index === steps.length - 1 ? (
                    <Button
                      variant="contained"
                      onClick={handleComplete}
                      startIcon={<CheckCircleIcon />}
                      disabled={loading}
                    >
                      Einrichtung abschließen
                    </Button>
                  ) : index === 2 ? (
                    <Button
                      variant="contained"
                      onClick={handleVerifyCode}
                      disabled={verificationCode.length !== 6 || isVerifying}
                    >
                      Code verifizieren
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      disabled={index === 1 && !qrCode}
                    >
                      Weiter
                    </Button>
                  )}

                  {index > 0 && (
                    <Button onClick={handleBack} disabled={loading || isVerifying}>
                      Zurück
                    </Button>
                  )}

                  <Button onClick={onCancel} disabled={loading || isVerifying}>
                    Abbrechen
                  </Button>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </CardContent>
    </Card>
  );
};

export default TwoFactorSetup;
